import React, { Component, ReactNode, useEffect, useState } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

/**
 * Wraps React.lazy() imports with retry logic and offline-friendly error handling.
 * When a dynamic import fails (e.g., offline and chunk not cached), shows a
 * helpful message instead of a white screen.
 */

// Retry a dynamic import up to `retries` times with exponential backoff
export function lazyWithRetry(
  factory: () => Promise<{ default: React.ComponentType<any> }>,
  retries = 2
): React.LazyExoticComponent<React.ComponentType<any>> {
  return React.lazy(() =>
    factory().catch((err) => {
      if (retries > 0 && navigator.onLine) {
        // Online but failed (e.g., deploy changed chunk hash) — retry after short delay
        return new Promise<{ default: React.ComponentType<any> }>((resolve) => {
          setTimeout(() => resolve(lazyWithRetry(factory, retries - 1) as any), 1000);
        });
      }
      throw err; // Let error boundary handle it
    })
  );
}

interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

/**
 * Error boundary specifically for lazy-load failures.
 * Shows an offline-friendly message with auto-retry when connection returns.
 */
export class LazyOfflineBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Only catch dynamic import errors
    if (
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('error loading dynamically imported module')
    ) {
      return { hasError: true, error };
    }
    throw error; // Re-throw non-import errors to parent boundary
  }

  componentDidCatch() {
    // Intentionally empty — getDerivedStateFromError handles state
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <OfflineFallbackUI onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

function OfflineFallbackUI({ onRetry }: { onRetry: () => void }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-retry after 1s when connection returns
      setTimeout(onRetry, 1000);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [onRetry]);

  // Auto-retry every 3s when online — the chunk may have been cached by preload in the meantime
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(onRetry, 3000);
    return () => clearInterval(interval);
  }, [isOnline, onRetry]);

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50 p-4">
      <div className="max-w-sm w-full text-center space-y-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-orange-100">
            <WifiOff className="w-10 h-10 text-orange-600" />
          </div>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          Pagina non disponibile offline
        </h2>
        <p className="text-sm text-gray-600">
          {isOnline
            ? 'Connessione ripristinata. Ricaricamento in corso...'
            : 'Questa pagina non è stata ancora scaricata. Visita prima /shop online per abilitare l\'uso offline.'}
        </p>
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Riprova
        </button>
      </div>
    </div>
  );
}
